import { Users, AlertCircle, CheckCircle2, Download } from 'lucide-react';
import { toast } from 'sonner';
import { exportRACIPDF } from '@/utils/exportUtils';

const roles = [
  { person: 'J. Mueller', role: 'AI Risk Owner', models: 'GPT-4o, Claude Sonnet', frameworks: 'EU AI Act, ISO 42001', lastReview: 'Apr 10', status: 'active' },
  { person: 'S. Chen', role: 'Data Protection Officer', models: 'All models', frameworks: 'EU AI Act Art. 5, GDPR', lastReview: 'Apr 12', status: 'active' },
  { person: 'K. Patel', role: 'Model Steward', models: 'Mistral 7B, Internal Summarizer', frameworks: 'ISO 42001', lastReview: 'Apr 8', status: 'active' },
  { person: 'Legal Team', role: 'Legal Reviewer', models: 'Claude Sonnet', frameworks: 'EU AI Act Art. 13', lastReview: 'Apr 5', status: 'active' },
  { person: 'Compliance Team', role: 'Internal Audit Lead', models: 'All models', frameworks: 'All frameworks', lastReview: 'Apr 1', status: 'active' },
  { person: 'IT Security', role: 'CISO Delegate', models: 'All models', frameworks: 'NIST AI RMF', lastReview: 'Mar 28', status: 'active' },
  { person: '—', role: 'Model Steward', models: 'GPT-4o Mini', frameworks: 'None', lastReview: 'Never', status: 'unassigned' },
];

const raciRoles = ['AI Risk Owner', 'DPO', 'Model Steward', 'Legal Reviewer', 'CISO Delegate'];
const raciObligations = [
  { obligation: 'Risk Management (Art.9)', cells: ['A', 'C', 'R', 'I', 'C'] },
  { obligation: 'Data Governance (Art.10)', cells: ['C', 'A', 'R', 'I', 'C'] },
  { obligation: 'Technical Documentation (Art.11)', cells: ['R', 'I', 'A', 'C', 'I'] },
  { obligation: 'Transparency (Art.13)', cells: ['C', 'R', 'C', 'A', 'I'] },
  { obligation: 'Human Oversight (Art.14)', cells: ['A', 'C', 'R', 'C', 'R'] },
  { obligation: 'Incident Reporting (Art.73)', cells: ['R', 'C', 'I', 'C', 'A'] },
];

const raciBadge = (val) => {
  const isRA = val === 'R' || val === 'A';
  return (
    <span className={`inline-flex items-center justify-center w-6 h-6 rounded text-[10px] font-bold ${isRA ? 'bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/20' : 'bg-[#1E2A45] text-[#475569]'}`}>
      {val}
    </span>
  );
};

export default function RolesResponsibilities() {
  return (
    <div data-testid="roles-responsibilities" className="space-y-6">
      <div>
        <div className="text-sm text-[#94A3B8] mb-1">AI Governance Roles & Accountability</div>
        <div className="text-xs text-[#64748B]">Map people and teams to AI models, workflows, and compliance obligations. Auditors require clear accountability chains — this is yours.</div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="glass-card p-4"><div className="text-[10px] text-[#475569] uppercase tracking-wider mb-1">Roles Defined</div><div className="text-2xl font-bold text-[#F8FAFC]">12</div></div>
        <div className="glass-card p-4"><div className="text-[10px] text-[#475569] uppercase tracking-wider mb-1">Models with Owners</div><div className="text-2xl font-bold text-[#F59E0B]">4/5</div><div className="text-[10px] text-[#F59E0B]">GPT-4o Mini unassigned</div></div>
        <div className="glass-card p-4"><div className="text-[10px] text-[#475569] uppercase tracking-wider mb-1">Named DPO</div><div className="text-2xl font-bold text-[#10B981]">3/3</div></div>
        <div className="glass-card p-4"><div className="text-[10px] text-[#475569] uppercase tracking-wider mb-1">Accountability Gaps</div><div className="text-2xl font-bold text-[#EF4444]">1</div></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Roles Table */}
        <div className="lg:col-span-3 glass-card overflow-hidden" data-testid="roles-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-[#1E2A45]">
                {['Person / Team', 'Role', 'Models', 'Frameworks', 'Last Review', 'Status'].map(h => (
                  <th key={h} className="text-left text-[10px] font-medium text-[#475569] uppercase tracking-wider py-3 px-3">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {roles.map((r, i) => {
                  const isUnassigned = r.status === 'unassigned';
                  return (
                    <tr key={i} className={`border-b border-[#1E2A45]/50 ${isUnassigned ? 'bg-[#EF4444]/[0.03]' : ''}`} data-testid={`role-row-${i}`}>
                      <td className={`py-2.5 px-3 text-xs font-medium ${isUnassigned ? 'text-[#EF4444]' : 'text-[#F8FAFC]'}`}>{r.person}</td>
                      <td className="py-2.5 px-3 text-xs text-[#94A3B8]">{r.role}</td>
                      <td className="py-2.5 px-3 text-xs text-[#64748B] max-w-[140px] truncate">{r.models}</td>
                      <td className="py-2.5 px-3 text-xs text-[#64748B] max-w-[120px] truncate">{r.frameworks}</td>
                      <td className="py-2.5 px-3 text-xs text-[#475569]">{r.lastReview}</td>
                      <td className="py-2.5 px-3">
                        {isUnassigned ? (
                          <button data-testid="assign-owner-gpt4o-mini" onClick={() => toast.info('Opening owner assignment for GPT-4o Mini')} className="text-[10px] text-[#6366F1] hover:text-[#818cf8] border border-[#6366F1]/30 px-2 py-0.5 rounded transition-colors">Assign owner</button>
                        ) : (
                          <span className="flex items-center gap-1 text-[10px] text-[#10B981]"><CheckCircle2 size={11} />Active</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* RACI Matrix */}
        <div className="lg:col-span-2 glass-card p-5" data-testid="raci-matrix">
          <h4 className="text-xs font-semibold text-[#F8FAFC] uppercase tracking-wider mb-3">Responsibility Matrix — EU AI Act</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="border-b border-[#1E2A45]">
                <th className="text-left text-[9px] text-[#475569] py-2 pr-2">Obligation</th>
                {raciRoles.map(r => <th key={r} className="text-center text-[8px] text-[#475569] py-2 px-1 whitespace-nowrap">{r}</th>)}
              </tr></thead>
              <tbody>
                {raciObligations.map((row) => (
                  <tr key={row.obligation} className="border-b border-[#1E2A45]/50">
                    <td className="py-2 pr-2 text-[10px] text-[#94A3B8]">{row.obligation}</td>
                    {row.cells.map((cell, ci) => (
                      <td key={ci} className="py-2 px-1 text-center">{raciBadge(cell)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 pt-3 border-t border-[#1E2A45] flex items-center justify-between flex-wrap gap-2">
            <div className="text-[10px] text-[#475569]">Last reviewed: Apr 1, 2026 · Next: Jul 1, 2026</div>
            <button onClick={() => { exportRACIPDF(); toast.success('RACI matrix downloaded as PDF'); }} className="flex items-center gap-1 text-[10px] text-[#6366F1] hover:text-[#818cf8] transition-colors"><Download size={11} />Download RACI as PDF</button>
          </div>
          <div className="flex gap-3 mt-2 text-[9px] text-[#475569]">
            <span><span className="inline-flex w-4 h-4 items-center justify-center rounded bg-[#10B981]/15 text-[#10B981] text-[8px] font-bold mr-1">R</span>Responsible</span>
            <span><span className="inline-flex w-4 h-4 items-center justify-center rounded bg-[#10B981]/15 text-[#10B981] text-[8px] font-bold mr-1">A</span>Accountable</span>
            <span><span className="inline-flex w-4 h-4 items-center justify-center rounded bg-[#1E2A45] text-[#475569] text-[8px] font-bold mr-1">C</span>Consulted</span>
            <span><span className="inline-flex w-4 h-4 items-center justify-center rounded bg-[#1E2A45] text-[#475569] text-[8px] font-bold mr-1">I</span>Informed</span>
          </div>
        </div>
      </div>
    </div>
  );
}
